import {Filter, FilterDef, FilterType, FilterOperator} from "./types";

/**
 * Rewrites generically all DATE filters from
 *   ```someColumn = "YYYY-MM-DD"```
 * into ranges covering the whole days:
 *   ```someColumn >= YYYY-MM-DDT00:00:00.000Z AND someColumn <  nextDayT00:00:00.000Z```
 *
 * Only applies when the filter type is DATE. Everything else is returned unchanged.
 */
export function rewriteFiltersForOneDayWorkaround<T>(
    filterDefs: FilterDef<T>[] | undefined,
    filters: Filter[]
): Filter[] {
    const rewritten: Filter[] = [];

    for (const flt of filters) {
        const def = filterDefs?.find((d) => String(d.fieldName) === flt.f);

        if (def?.type === FilterType.DATE && flt.o === FilterOperator.EQ && typeof flt.v === "string") {
            const dateStr = flt.v as string;
            const m = /^(\d{4}-\d{2}-\d{2})/.exec(dateStr);

            if (m) {
                const start = new Date(`${m[1]}T00:00:00.000Z`);
                const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);

                rewritten.push({
                    f: flt.f,
                    o: FilterOperator.GTE,
                    v: start.toISOString(),
                });
                rewritten.push({
                    f: flt.f,
                    o: FilterOperator.LT,
                    v: end.toISOString(),
                });

                continue;
            }
        }

        rewritten.push(flt);
    }

    return rewritten;
}

/**
 * Rewrites LTE (<=) DATE filters so the time is set to the end of the day:
 *   YYYY-MM-DD      -> YYYY-MM-DDT23:59:59.000Z
 * Only applies when the filter type is DATE and operator is LTE.
 *
 * other DATE filters are rewritten to include time if the time is missing
 *   YYYY-MM-DD      -> YYYY-MM-DDT00:00:00.000Z
 *
 * Everything else is kept as is.
 */
export function rewriteFiltersToEndOfDay<T>(filterDefs: FilterDef<T>[] | undefined, filters: Filter[]): Filter[] {
    const rewritten: Filter[] = [];

    for (const flt of filters) {
        const def = filterDefs?.find((d) => String(d.fieldName) === flt.f);

        if (def?.type === FilterType.DATE && typeof flt.v === "string") {
            const raw = flt.v as string;

            const m = /^(\d{4})-(\d{2})-(\d{2})(.*)/.exec(raw);

            if (m) {
                const oldExt = m[4];
                const ext =
                    flt.o === FilterOperator.LTE || flt.o === FilterOperator.GT
                        ? "T23:59:59.000Z"
                        : oldExt.length > 0
                        ? oldExt
                        : "T00:00:00.000Z";
                const yyyy = m[1];
                const mm = m[2];
                const dd = m[3];
                const endOfDay = `${yyyy}-${mm}-${dd}${ext}`;
                rewritten.push({
                    ...flt,
                    v: endOfDay,
                });

                continue;
            }

            rewritten.push(flt);
            continue;
        }

        rewritten.push(flt);
    }

    return rewritten;
}
